/**
 * Colaboradores Router Hooks
 *
 * Custom hooks for the colaboradores (workforce dashboard) router endpoints.
 * These provide typed access to employee/payroll data while the colaboradores router
 * types are not yet included in the @ds/ds-api-types package.
 *
 * Backend source: packages/serverless/api/src/modules/monumenta-trpc/handlers/colaboradores/router.ts
 */

import { useQuery } from '@tanstack/react-query'
import { get } from 'aws-amplify/api'

// ============================================================================
// Amplify fetch helper (same pattern as progress-hooks.ts)
// ============================================================================

async function fetchColaboradores<T>(path: string): Promise<T> {
  const apiOperation = get({
    apiName: 'monumenta-trpc',
    path,
  })

  const response = await apiOperation.response
  const body = await response.body.text()
  const parsed = JSON.parse(body)

  // tRPC wraps GET results in { result: { data: ... } }
  if (parsed.result?.data) {
    return parsed.result.data as T
  }
  if (parsed.error) {
    throw new Error(parsed.error.message || 'Unknown error')
  }
  return parsed as T
}

// ============================================================================
// Types (mirroring backend router output shapes)
// ============================================================================

export interface DashboardOutput {
  headcount: {
    total: number
    active: number
    terminated: number
    avgSalary: number
    hiredLast90Days: number
    terminatedLast90Days: number
  }
  latestPayroll: {
    period: string
    totalCprov: number
    totalCrel: number
    totalSalary: number
    avgCostPerEmployee: number
  } | null
  contractDistribution: Array<{
    type: string
    count: number
    totalSalary: number
  }>
  departmentDistribution: Array<{
    id: number
    nome: string
    count: number
    totalCost: number
  }>
}

export interface PayrollTrendMonth {
  period: string
  periodLabel: string
  headcount: number
  totalCprov: number
  totalCrel: number
  totalSalary: number
  totalBenefits: number
  totalTaxes: number
  totalProvisions: number
  avgCostPerEmployee: number
}

export interface PayrollTrendOutput {
  months: PayrollTrendMonth[]
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all-in-one dashboard data (headcount + payroll totals + distributions)
 */
export function useColaboradoresDashboard(options?: { enabled?: boolean }) {
  return useQuery<DashboardOutput>({
    queryKey: ['colaboradores', 'getDashboard'],
    queryFn: () => fetchColaboradores<DashboardOutput>('/colaboradores/getDashboard'),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

// ============================================================================
// Employee types
// ============================================================================

export interface EmployeeListItem {
  id: number
  funcionario: string
  email: string | null
  contrato: string | null
  situacao: string | null
  setor: number | null
  setorNome: string | null
  salario: number | null
  admissao: string | null
  dtdemiss: string | null
  funcao: number | null
  funcaoNome: string | null
}

export interface EmployeeListOutput {
  employees: EmployeeListItem[]
  total: number
}

export interface EmployeeDetailOutput {
  employee: EmployeeListItem & {
    op_combustivel: boolean | null
    combustivel: number | null
    op_alimenta: boolean | null
    alimentacao: number | null
    saude: number | null
    AuxTel: number | null
    pj_r_moradia: number | null
  }
  department: { id: number; setor: string } | null
  recentPayroll: Array<{
    mesano: string
    salario: number | null
    cprov: number | null
    crel: number | null
    combustivel: number | null
    alimentacao: number | null
    saude: number | null
  }>
  recentPayments: Array<{
    mesFolha: string
    Salario: number
    total: number
  }>
}

export interface DepartmentItem {
  id: number
  nome: string
  headcount: number
  totalSalary: number
  contractBreakdown: Record<string, number>
}

export interface ListEmployeesInput {
  status?: 'A' | 'D' | 'all'
  contractType?: string
  departmentId?: number
  search?: string
  take?: number
  skip?: number
  orderBy?: 'name' | 'salary' | 'admissao' | 'setor'
}

/**
 * Fetch paginated, filterable employee list
 */
export function useColaboradoresListEmployees(
  input?: ListEmployeesInput,
  options?: { enabled?: boolean }
) {
  const params = input || {}
  const inputParam = encodeURIComponent(JSON.stringify(params))
  const path = `/colaboradores/listEmployees?input=${inputParam}`

  return useQuery<EmployeeListOutput>({
    queryKey: ['colaboradores', 'listEmployees', params],
    queryFn: () => fetchColaboradores<EmployeeListOutput>(path),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetch single employee detail with recent payroll
 */
export function useColaboradoresGetEmployee(
  id: number | null,
  options?: { enabled?: boolean }
) {
  const inputParam = encodeURIComponent(JSON.stringify({ id }))
  const path = `/colaboradores/getEmployee?input=${inputParam}`

  return useQuery<EmployeeDetailOutput>({
    queryKey: ['colaboradores', 'getEmployee', id],
    queryFn: () => fetchColaboradores<EmployeeDetailOutput>(path),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: (options?.enabled ?? true) && id !== null,
  })
}

/**
 * Fetch departments with headcount and salary breakdown
 */
export function useColaboradoresListDepartments(options?: { enabled?: boolean }) {
  return useQuery<DepartmentItem[]>({
    queryKey: ['colaboradores', 'listDepartments'],
    queryFn: async () => {
      const result = await fetchColaboradores<{ departments: DepartmentItem[] }>('/colaboradores/listDepartments')
      return result.departments
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetch monthly payroll trend (cprov, crel, benefits, taxes)
 */
export function useColaboradoresPayrollTrend(
  input?: { months?: number },
  options?: { enabled?: boolean }
) {
  const months = input?.months ?? 12
  // tRPC sends input as JSON in query string for GET requests
  const inputParam = encodeURIComponent(JSON.stringify({ months }))
  const path = `/colaboradores/getPayrollTrend?input=${inputParam}`

  return useQuery<PayrollTrendOutput>({
    queryKey: ['colaboradores', 'getPayrollTrend', { months }],
    queryFn: () => fetchColaboradores<PayrollTrendOutput>(path),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}
